import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/features/auth/store/auth.store";

/**
 * Sends an already-onboarded user away from an onboarding form.
 *
 * The route guard decides on the persisted user before `refreshUser()`
 * answers, so a stale `profileCompleted: false` lands an onboarded user on
 * their role's onboarding form, and onboarding routes hide the app shell, so
 * nothing sends them on again. Submitting then either answers 409 (designer,
 * contractor) or overwrites the saved profile with the form (homeowner,
 * realtor). This leaves for `destination` as soon as the store says the
 * profile exists.
 *
 * The page's own successful create flips the same flag, and that must not
 * count: call the returned function in place of `markProfileComplete()` on
 * the success branch, and the page stays for its next step.
 */
export function useLeaveOnceOnboarded(destination: string): () => void {
	const navigate = useNavigate();
	const profileCompleted = useAuthStore(
		(s) => s.user?.profileCompleted === true,
	);
	const completedHereRef = useRef(false);

	useEffect(() => {
		if (profileCompleted && !completedHereRef.current) {
			navigate(destination, { replace: true });
		}
	}, [profileCompleted, navigate, destination]);

	return useCallback(() => {
		completedHereRef.current = true;
		useAuthStore.getState().markProfileComplete();
	}, []);
}
